import { ChangeEvent, ReactElement } from "react";
import {
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";

import {
  SecondaryDropdown,
  SecondaryMultiSelect,
} from "~frontend/components/SecondaryDropdown";

import Select, { BaseSelectProps, MultiSelect } from "../FormSelect";

export function RhfSelect<T extends FieldValues>(
  props: BaseSelectProps &
    UseControllerProps<T> & {
      multiple?: boolean;
      value?: string | string[];
      withNoneOption?: boolean;
      noneOptionLabel?: string;
      bottomLabel?: string;
      secondary?: boolean;
      onChange?: (value: unknown) => void;
    }
): ReactElement {
  const { control, rules, shouldUnregister, name, secondary, ...selectProps } =
    props;
  const { field, fieldState } = useController({
    control,
    rules,
    shouldUnregister,
    name,
  });

  const coreParams = {
    onBlur: field.onBlur,
    name: field.name,
    error: Boolean(fieldState.error),
    inputRef: field.ref,
    helperText: fieldState.error ? fieldState.error.message : props.bottomLabel,
    variant: secondary ? ("underlined" as const) : undefined,
  };

  if (props.multiple) {
    const Component = secondary ? SecondaryMultiSelect : MultiSelect;

    return (
      <Component
        {...coreParams}
        {...selectProps}
        onChange={(event: ChangeEvent<{ value: string[] }>) => {
          field.onChange(event.target.value);
          props.onChange?.(event.target.value);
        }}
        value={(field.value as string[]) ?? []}
      />
    );
  }

  const Component = secondary ? SecondaryDropdown : Select;

  return (
    <Component
      {...coreParams}
      {...selectProps}
      onChange={(event: ChangeEvent<{ value: string }>) => {
        field.onChange(event.target.value);
        props.onChange?.(event.target.value);
      }}
      value={(field.value as string) ?? ""}
    />
  );
}
